import { useCallback } from "react";

import { usePromise } from "@touchtech/web-hooks";

import { LocalStorageKeys } from "@is/constants";
import { useStoreEvents } from "@is/hooks";
import { LocalStorageService, api } from "@is/services";
import { TrackingEventTypes } from "@is/services/EventTracking";
import { createAddParkedBasketUrl } from "@is/services/api/urlBuilders/createBasketUrls";

type AddParkedBasketState = {
  isPending: boolean;
  error: Error | null;
  data: ParkedPayload | null;
};

type ParkedPayload = {
  sessionName: string;
  passcode?: string;
  metadata: Record<string, unknown>;
  deviceType: string;
  deviceName: string;
};

async function handleBasketUpdate(
  payload: ParkedPayload
): Promise<ParkedPayload> {
  const addParkedBasketUrl = createAddParkedBasketUrl();
  const data = (await api.post(addParkedBasketUrl, payload)) as ParkedPayload;
  return data;
}

export const useAddParkedBasket = (): [
  AddParkedBasketState,
  (payload: ParkedPayload) => Promise<ParkedPayload>
] => {
  const [{ isPending, error, data }, dispatch] = usePromise<ParkedPayload>();
  const { storeEvent } = useStoreEvents();

  const addParkedBasket = useCallback(
    async (payload: ParkedPayload) => {
      const sessionId = LocalStorageService.getItem(LocalStorageKeys.SessionId);

      storeEvent({
        eventType: TrackingEventTypes.ParkBasket,
        data: {
          sessionId,
          sessionName: payload.sessionName,
        },
        sessionId,
        createdAt: new Date().toISOString(),
      });

      const data = await dispatch(handleBasketUpdate(payload));
      return data;
    },
    [dispatch, storeEvent]
  );

  return [{ isPending, error, data }, addParkedBasket];
};
